/* ============================================================
   音效引擎（第二版）
   ============================================================

   第一版是 64 行、兩個原語（掃頻振盪器 + 濾波雜訊）、16 個嗶聲，
   而且**所有聲音都是單聲道、滿音量、不隨距離衰減、每一發一模一樣**。
   它跟第三版的柔邊 VFX 是同一種毛病：用的是 8-bit 的語彙，
   而這個遊戲的畫面是硬邊工業機甲。

   ## 音色方向：撞擊優先・極簡（使用者 2026-08-05 拍板）

   每個音只有兩層：

     ① **瞬態**：極短的濾波雜訊（5–15 ms）。這一層決定「有沒有打到」。
     ② **本體**：一個往下掉的低頻。這一層決定「有多重」。

   沒有第三層、沒有旋律、沒有殘響尾巴。理由跟畫面一樣 ——
   300 隻同屏、每秒十幾個發聲事件，任何多餘的一層都會疊成一團糊。
   乾、狠、不吐。

   ## 這一層負責的四件事（都是第一版沒有的）

   1. **分類匯流排**：武器／敵人／世界／UI 各一條，各自音量與壓縮。
      沒有匯流排就沒法平衡 —— 一調就是整體變大聲。
   2. **發聲上限**：同時發聲數封頂，滿了就丟掉新的（不是丟舊的 ——
      正在響的那個已經被聽見了，砍掉它會產生喀噠聲）。
   3. **距離衰減與左右聲相**：畫面外的爆炸不該跟腳邊的一樣大聲。
   4. **每發變化**：音高、濾波點、起音時間每一發小幅隨機。
      **這是「機槍感重複」唯一的治法**，而程序合成做這件事是免費的。

   ## 決定性

   這裡的 `Math.random()` **不可以**換成 `core/rng` 的 RNG ——
   那條是對局亂數，動它會改變模擬結果（lessons 2026-08-01）。
   音效不進快照，用系統亂數是對的。 */

var ctx: any = null;
var master: any = null;
var comp: any = null;
/* 音效總量與音樂總量。分開的理由見 `initEngine` 裡的訊號路徑說明。 */
var sfxG: any = null;
var musicG: any = null;
var noiseBuf: any = null;
var READY = false;

/** 四條匯流排。分開的理由是**平衡**：主砲每秒 3.75 發、命中每秒十幾次，
    而 UI 音一場只有幾十次 —— 它們不可能共用一個音量。 */
export var BUS: Record<string, any> = {};
var BUS_IDS = ['weapon', 'enemy', 'world', 'ui'];

/** 使用者音量（0–1），存 localStorage。`sfx` 是總音效音量，
    `music` 先留位子（這一輪還沒有音樂）。 */
export var VOL = { master: 0.9, sfx: 0.8, music: 0.7 };

/* 同時發聲上限。實測基準：主砲 3.75/s + 命中十幾次/s + 掛載，
   尖峰約 25 個聲音同時衰減中。上限 24 是「聽得出被砍掉」的邊界之上一點。
   **滿了丟新的不丟舊的**：正在響的已經被聽見，中途砍掉會有喀噠聲。 */
var MAX_VOICES = 24, voices = 0;

/** 聽者（＝鏡頭跟隨的玩家位置）。由 `game.ts` 每幀更新。 */
var LX = 0, LZ = 0;
export function setListener(x: number, z: number) { LX = x; LZ = z; }

/** 聽得到的半徑。超過就完全不發聲 —— 這同時是效能措施：
    畫面外的敵人爆炸連 voice 都不佔。 */
var HEAR = 48;

export function audioReady() { return READY; }

/** `c` 給的話直接用它（`OfflineAudioContext` 走這條，離線自查用）。 */
export function initEngine(c?: any) {
  if (ctx) return READY;
  if (c) ctx = c;
  else {
    if (typeof window === 'undefined') return false;    // headless：整層停用
    var AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return false;
    ctx = new AC();
  }
  /* 訊號路徑（2026-08-06 加音樂之後）：

       四條音效匯流排 → 壓縮器 → 音效總量 → ┐
                          音樂匯流排 → 音樂總量 → ┴→ 主音量 → 輸出

     兩個要點：
     1. **音樂不經過音效的壓縮器。** 那顆壓縮器是為了壓「同時很多聲音」而設的
        （閾值 −18 dB、比例 6:1），音樂餵進去會被每一次爆炸壓下去一大塊。
     2. **音效音量不再掛在主音量上。** 舊版是 `master = VOL.master * VOL.sfx`，
        當時沒有音樂所以看不出問題 —— 現在拉「音效」滑桿會連音樂一起拉。
        改成各自一顆 gain，兩條路獨立。
     壓縮器仍放在音效總量之前：它要壓的是密度，不該隨使用者調音量而改變作用量。 */
  master = ctx.createGain();
  master.gain.value = VOL.master;
  master.connect(ctx.destination);

  sfxG = ctx.createGain();
  sfxG.gain.value = VOL.sfx;
  sfxG.connect(master);
  comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -18; comp.knee.value = 12;
  comp.ratio.value = 6; comp.attack.value = 0.003; comp.release.value = 0.12;
  comp.connect(sfxG);
  for (var i = 0; i < BUS_IDS.length; i++) {
    var g = ctx.createGain();
    g.gain.value = 1;
    g.connect(comp);
    BUS[BUS_IDS[i]] = g;
  }

  musicG = ctx.createGain();
  musicG.gain.value = VOL.music;
  musicG.connect(master);
  BUS.music = musicG;
  /* 白噪緩衝：0.6 秒夠用（最長的瞬態也只有 30 ms），
     所有雜訊層共用同一段、從隨機位置起播 —— 這樣連「雜訊本身」都不會重複。 */
  noiseBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.6), ctx.sampleRate);
  var d = noiseBuf.getChannelData(0);
  for (var k = 0; k < d.length; k++) d[k] = Math.random() * 2 - 1;
  loadVol();
  READY = true;
  return true;
}

/** 清空引擎狀態，讓下一次 `initEngine()` 重建。

    離線自查要換 context（`OfflineAudioContext`），算完要換回來 ——
    **換回來那一步不能忘**，否則之後按任何按鈕都沒有聲音，
    而「按了沒反應」看起來會像配方壞掉。 */
export function resetEngine() { ctx = null; READY = false; BUS = {}; voices = 0; sfxG = null; musicG = null; }

/** 三顆音量一次套用。**三條路各自一顆 gain** —— 舊版把 sfx 乘進 master，
    加了音樂之後那會讓「音效」滑桿連音樂一起拉。 */
function applyVol() {
  if (master) master.gain.value = VOL.master;
  if (sfxG) sfxG.gain.value = VOL.sfx;
  if (musicG) musicG.gain.value = VOL.music;
}

/** 音樂層要自己排程聲部，需要 context 與它的匯流排（`platform/music.ts`）。 */
export function audioCtx() { return ctx; }
export function musicBus() { return musicG; }

export function setVolume(k: 'master' | 'sfx' | 'music', v: number) {
  VOL[k] = Math.max(0, Math.min(1, v));
  applyVol();
  try { localStorage.setItem('chassis.vol', JSON.stringify(VOL)); } catch (e) { }
}
function loadVol() {
  try {
    var s = localStorage.getItem('chassis.vol');
    if (!s) return;
    var o = JSON.parse(s);
    for (var k in VOL) if (typeof o[k] === 'number') (VOL as any)[k] = o[k];
    applyVol();
  } catch (e) { }
}

/** 世界座標 → 音量與左右。**不是 PannerNode**：俯視遊戲只需要左右，
    而 HRTF 在 300 隻同屏時是純虧的成本。 */
function place(x: number | undefined, z: number | undefined) {
  if (x === undefined || z === undefined) return { g: 1, p: 0 };
  var dx = x - LX, dz = z - LZ;
  var d = Math.hypot(dx, dz);
  if (d > HEAR) return { g: 0, p: 0 };
  /* 平方衰減聽起來太急（近的爆炸會蓋掉一切），線性又太平。
     取 1 − (d/HEAR)^1.6：近處保持份量，遠處掉得夠快。 */
  return { g: Math.max(0, 1 - Math.pow(d / HEAR, 1.6)), p: Math.max(-1, Math.min(1, dx / 26)) };
}

export type Voice = {
  /** 匯流排。 */
  bus?: string;
  /** 世界座標。不給就是「貼在耳邊」（UI 音、玩家自己的動作）。 */
  x?: number; z?: number;
  /** 音量倍率。 */
  vol?: number;
  /** **升階**（0 起跳）。同一顆音在連段／連續命中裡越後面越「上去」。
      各音包自己詮釋這個數字：`tonal` 往上取音階（旋律感），
      `thud` 加重、壓低、拉長（份量感）—— 兩種音包的美學不同，
      硬性規定同一個物理意義只會讓其中一邊變難聽。
      依據：`docs/game-enjoy/06-game-feel.md` §三「音高遞增」。 */
  step?: number;
  /** **這個音代表幾個事件**（`audio-density.ts` 的聚合結果，1 起跳）。
      一次殺十隻該聽起來像「殺了一片」，不是十個一樣的音搶同一個頻段。
      配方自己詮釋 —— 但**不要靠加音量**表達數量，那會回到低頻堆積的老問題。 */
  n?: number;
  /** 這是值得單獨聽見的事件（Boss／精英／當前鎖定目標）。
      配方可以據此讓它跳出雜兵的底噪。 */
  imp?: boolean;
};

/** 借一個發聲名額。回 false 代表這一幀已經滿了，呼叫端整個事件放棄。 */
function take() {
  if (!READY || voices >= MAX_VOICES) return false;
  voices++;
  return true;
}
function done() { voices = Math.max(0, voices - 1); }
export function voiceCount() { return voices; }

/** 隨機微擾。`amt` 0.06 = ±6%。**每一發都要用它**，否則就是機槍感。 */
export function vary(v: number, amt: number) { return v * (1 + (Math.random() * 2 - 1) * amt); }

function chain(o: Voice, g: any) {
  var pos = place(o.x, o.z);
  if (pos.g <= 0) return null;
  var vg = ctx.createGain();
  vg.gain.value = (o.vol === undefined ? 1 : o.vol) * pos.g;
  var pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
  if (pan) { pan.pan.value = pos.p; g.connect(vg); vg.connect(pan); pan.connect(BUS[o.bus || 'world']); }
  else { g.connect(vg); vg.connect(BUS[o.bus || 'world']); }
  return vg;
}

/** ① 瞬態：一段極短的濾波雜訊。**這一層決定「有沒有打到」。**

    `f0 → f1` 是濾波器的掃頻（不是音高）：從亮掃到暗，
    聽起來就是「一下敲擊」而不是「一段噪音」。 */
export function transient(o: Voice & {
  dur?: number; f0?: number; f1?: number; q?: number; type?: BiquadFilterType;
}) {
  if (!take()) return;
  var dur = o.dur === undefined ? 0.012 : o.dur;
  var s = ctx.createBufferSource();
  s.buffer = noiseBuf;
  /* 從隨機位置起播：同一段白噪重複播放其實聽得出來（尤其每秒好幾次）。 */
  var off = Math.random() * 0.5;
  var f = ctx.createBiquadFilter();
  f.type = o.type || 'bandpass';
  f.Q.value = o.q === undefined ? 1.2 : o.q;
  var t = ctx.currentTime;
  f.frequency.setValueAtTime(Math.max(60, o.f0 === undefined ? 3200 : o.f0), t);
  f.frequency.exponentialRampToValueAtTime(Math.max(60, o.f1 === undefined ? 900 : o.f1), t + dur);
  var g = ctx.createGain();
  /* 起音 1.5 ms 不是 0：真正的瞬間起音會產生喀噠（DC 跳變），
     而且在壓縮器前面會觸發過度的 gain reduction。 */
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(1, t + 0.0015);
  g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
  s.connect(f); f.connect(g);
  var out = chain(o, g);
  if (!out) { done(); return; }
  s.onended = done;
  s.start(t, off, dur + 0.02);
  s.stop(t + dur + 0.02);
}

/** ③ 撥弦：一個**有音高**的短音，經過低通濾波器。

    給「有調性」那個方向用。跟 `body` 的差別是它**不掃音高**（音高就是音高，
    那是它的意義），而是掃**濾波器**：起音亮、立刻悶下去 —— 那就是撥彈的
    衰減，也是「不刺」的關鍵：高頻只在最前面 10 ms 出現。

    `detune` 給的話疊第二個振盪器並微微失諧，聽起來會厚一階
    （單一振盪器在低音量下很薄，容易讀成電子錶的嗶聲）。 */
export function pluck(o: Voice & {
  f?: number; dur?: number; cut0?: number; cut1?: number; type?: OscillatorType; detune?: number;
}) {
  if (!take()) return;
  var dur = o.dur === undefined ? 0.08 : o.dur;
  var t = ctx.currentTime;
  var f = Math.max(30, o.f === undefined ? 220 : o.f);
  var lp = ctx.createBiquadFilter();
  lp.type = 'lowpass'; lp.Q.value = 0.9;
  lp.frequency.setValueAtTime(Math.max(120, o.cut0 === undefined ? 2600 : o.cut0), t);
  lp.frequency.exponentialRampToValueAtTime(
    Math.max(120, o.cut1 === undefined ? 520 : o.cut1), t + dur * 0.5);
  var g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(1, t + 0.003);
  g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
  var oscs: any[] = [];
  var n = o.detune ? 2 : 1;
  for (var i = 0; i < n; i++) {
    var osc = ctx.createOscillator();
    osc.type = o.type || 'triangle';
    osc.frequency.setValueAtTime(f, t);
    if (i) osc.detune.setValueAtTime(o.detune!, t);
    osc.connect(lp);
    oscs.push(osc);
  }
  lp.connect(g);
  var out = chain(o, g);
  if (!out) { done(); return; }
  oscs[0].onended = done;
  for (var k = 0; k < oscs.length; k++) { oscs[k].start(t); oscs[k].stop(t + dur + 0.02); }
}

/** ② 本體：一個往下掉的低頻。**這一層決定「有多重」。**

    音高從 `f0` 指數掉到 `f1` —— 掉得越快越像「撞擊」，
    掉得慢就變成「聲音」。極簡方向裡它永遠掉得很快。 */
export function body(o: Voice & {
  dur?: number; f0?: number; f1?: number; type?: OscillatorType; drop?: number;
}) {
  if (!take()) return;
  var dur = o.dur === undefined ? 0.09 : o.dur;
  var t = ctx.currentTime;
  var osc = ctx.createOscillator();
  osc.type = o.type || 'sine';
  osc.frequency.setValueAtTime(Math.max(20, o.f0 === undefined ? 220 : o.f0), t);
  /* 音高掉完的時間比音量短（`drop`，預設 0.55 倍）：先掉到底再讓尾音衰減，
     這樣尾巴是一個穩定的低音而不是一路滑下去的口哨。 */
  osc.frequency.exponentialRampToValueAtTime(
    Math.max(20, o.f1 === undefined ? 60 : o.f1), t + dur * (o.drop === undefined ? 0.55 : o.drop));
  var g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(1, t + 0.002);
  g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
  osc.connect(g);
  var out = chain(o, g);
  if (!out) { done(); return; }
  osc.onended = done;
  osc.start(t); osc.stop(t + dur + 0.02);
}
